import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect, Provider } from 'react-redux';
import { type RouterProps } from 'react-router';
import {
  BrowserRouter as Router,
  Redirect,
  Switch,
  withRouter,
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { makeStyles } from 'tss-react/mui';
import ConnectServer from './common/connect_service';
import NodeServer from './common/node_server';
import { setRouterHistory } from './common/routerHistory';
import ErrorBoundary from './components/error-boundary/Root';
import { useAppDispatch } from './hooks/redux';
import { type IPermissions } from './models/auth';
import { ProtectedRoute, Route } from './Router';
import { default as config } from './services/config';
import './services/sentry';
import { logout } from './store/thunks/auth';
import * as websiteActions from './store/actions/website';
import { getPermissions } from './store/selectors/user';
import { persistor, store } from './store/store';
import theme from './theme';
import { subscribe } from './store/actions/eventListener';
import { type AppState } from './types/store/states/AppState';

interface IPageProps extends AppState, RouterProps {
  permissions: IPermissions;
}
const Fallback = () => (
  <Grid container textAlign='center'>
    <Grid size={12}>
      <CircularProgress color='primary' size={100} />
    </Grid>
  </Grid>
);

const AccountDetails = React.lazy(() => import('./containers/AccountDetails'));
const Acknowledgements = React.lazy(
  () => import('./containers/Acknowledgements'),
);
const Batches = React.lazy(() => import('./containers/Batches'));
const Blocks = React.lazy(() => import('./containers/Blocks'));
const SearchCalls = React.lazy(() => import('./containers/Calls'));
const CommissaryManage = React.lazy(
  () => import('./containers/CommissaryManage'),
);
const CommissaryOrders = React.lazy(
  () => import('./containers/CommissaryOrders'),
);
const ControlCenter = React.lazy(() => import('./containers/ControlCenter'));
const InmateDetails = React.lazy(() => import('./containers/InmateDetails'));
const InmateNotifications = React.lazy(
  () => import('./containers/InmateNotification'),
);
const InmateRequests = React.lazy(() => import('./containers/InmateRequests'));
const Inmates = React.lazy(() => import('./containers/Inmates'));
const Login = React.lazy(() => import('./containers/Login'));
const NotFound = React.lazy(() => import('./containers/NotFound'));
const PrivateNumbers = React.lazy(() => import('./containers/PrivateNumbers'));
const Profile = React.lazy(() => import('./containers/Profile'));
const SearchTexts = React.lazy(() => import('./containers/SearchTexts'));
const SearchPostalMail = React.lazy(
  () => import('./containers/SearchPostalMail'),
);
const SearchVisitations = React.lazy(
  () => import('./containers/SearchVisitations'),
);
const SearchVoicemail = React.lazy(
  () => import('./containers/SearchVoicemail'),
);
const Users = React.lazy(() => import('./containers/Users'));
const VisitationManage = React.lazy(
  () => import('./containers/VisitationManage'),
);
const LiveStatus = React.lazy(
  () => import('./components/live-status/LiveStatus'),
);
const PageNotification = React.lazy(
  () => import('./components/layout/PageNotification'),
);
const Header = React.lazy(() => import('./components/header/Header'));
const Contact = React.lazy(() => import('./containers/Contact'));
const EventNotifications = React.lazy(
  () => import('./containers/EventNotifications'),
);
const HousingUnits = React.lazy(() => import('./containers/HousingUnits'));
const PersonSearch = React.lazy(() => import('./containers/PersonSearch'));
const ReferenceMaterials = React.lazy(
  () => import('./containers/ReferenceMaterials'),
);
const Reports = React.lazy(() => import('./containers/Reports'));
const Investigation = React.lazy(() => import('./containers/Investigation'));
const AIBriefing = React.lazy(() => import('./containers/AIBriefing'));
const WhatsNewBanner = React.lazy(
  () => import('./components/header/WhatsNewBanner'),
);
const WhatsNew = React.lazy(() => import('./containers/WhatsNew'));
const CaseReportList = React.lazy(() => import('./containers/CaseReportList'));
const CaseReport = React.lazy(() => import('./containers/CaseReport'));

const CardAccount = React.lazy(() => import('./containers/CardAccount'));
const useStyles = makeStyles()({
  content: {
    [`@media print`]: {
      paddingLeft: '0 !important',
    },
  },
});

const Page: React.FC<IPageProps> = ({
  common: { rehydrated, facility },
  auth,
  permissions,
  history,
}) => {
  const dispatch = useAppDispatch();
  const [navWidth, setNavWidth] = useState<number>();
  const { classes } = useStyles();

  useEffect(() => {
    setRouterHistory(history);
  }, [history]);

  useEffect(() => {
    // Retrieve the base website data
    if (
      rehydrated &&
      (facility == null || facility.communicationsEnabled === undefined)
    ) {
      dispatch(websiteActions.loadBaseData());
    }

    // Start the node server connection
    NodeServer.get();

    // Setup listeners to live feed
    if (permissions.hasPermission('text_message_search')) {
      dispatch(
        subscribe(
          `reliancetelephone.connect.text-message/facility/${facility?.id}`,
        ),
      );
    }

    // Retry connect once facility is loaded if the first attempt completed before the facility was loaded
    if (facility?.id) {
      NodeServer.get().connectToNode();
    }

    // Start connect core connection if applicable
    if (config.connectServer?.url) {
      ConnectServer.get();
    }
  }, [rehydrated, facility, dispatch, permissions]);

  let initialLoggedInRoute = '/search/calls';

  if (!permissions.hasPermission('search')) {
    initialLoggedInRoute = '/inmates';
  } else if (
    !config.hasOneOfFeatures([
      'audio-call',
      'video-call',
      'wireless-audio-calls',
    ])
  ) {
    if (config.hasFeature('texting')) {
      initialLoggedInRoute = '/search/texts';
    } else if (config.hasOneOfFeatures(['visitation', 'audio-visitation'])) {
      initialLoggedInRoute = '/search/visitation';
    } else if (config.hasFeature('voicemail')) {
      initialLoggedInRoute = '/search/voicemail';
    } else {
      initialLoggedInRoute = '/inmates';
    }
  }

  return (
    <React.Suspense fallback={Fallback()}>
      <CssBaseline />
      <PageNotification />

      {auth.loggedIn && (
        <Header
          name={config.name}
          facility={facility}
          user={auth.facilityUser}
          permissions={permissions}
          onWidthChange={setNavWidth}
          onLogout={() => {
            dispatch(logout(false));
          }}
        />
      )}

      <React.Suspense fallback={Fallback()}>
        <div
          style={{ paddingLeft: auth.loggedIn ? navWidth : 0 }}
          className={classes.content}
        >
          {auth.loggedIn && <WhatsNewBanner />}
          <Switch>
            <Redirect exact from='/' to={initialLoggedInRoute} />
            <Route exact path='/login' component={Login} />
            <ProtectedRoute path='/search/calls' component={SearchCalls} />
            <ProtectedRoute
              path='/search/voicemail'
              component={SearchVoicemail}
            />
            <ProtectedRoute path='/search/texts' component={SearchTexts} />
            <ProtectedRoute
              path='/search/mail-connect'
              component={SearchPostalMail}
            />
            <ProtectedRoute
              path='/search/visitations'
              component={SearchVisitations}
            />
            <ProtectedRoute
              path='/inmates/details/:account'
              component={InmateDetails}
            />
            <ProtectedRoute path='/account/:ani' component={AccountDetails} />
            <ProtectedRoute path='/inmates' component={Inmates} />
            <ProtectedRoute
              path='/control-center/:subPage?'
              component={ControlCenter}
            />
            <ProtectedRoute path='/private' component={PrivateNumbers} />
            <ProtectedRoute path='/blocks' component={Blocks} />
            <ProtectedRoute
              path='/inmate-notifications'
              component={InmateNotifications}
            />
            <ProtectedRoute
              path='/inmate-requests/:requestId?'
              component={InmateRequests}
            />
            <ProtectedRoute path='/batches/:batchId?' component={Batches} />
            <ProtectedRoute path='/live-status' component={LiveStatus} />
            <ProtectedRoute
              path='/visitations/manage'
              component={VisitationManage}
            />
            <ProtectedRoute path='/users' component={Users} />
            <ProtectedRoute path='/profile' component={Profile} />
            <ProtectedRoute
              path='/commissary/manage/:categoryId?'
              component={CommissaryManage}
            />
            <ProtectedRoute
              path='/commissary/orders'
              component={CommissaryOrders}
            />
            <ProtectedRoute
              path='/acknowledgements'
              component={Acknowledgements}
            />
            <ProtectedRoute path='/reports' component={Reports} />
            <ProtectedRoute path='/contact' component={Contact} />
            <ProtectedRoute path='/alerts' component={EventNotifications} />
            <ProtectedRoute path='/housing-units' component={HousingUnits} />
            <ProtectedRoute path='/person-search' component={PersonSearch} />
            <ProtectedRoute
              path='/reference-materials'
              component={ReferenceMaterials}
            />
            <ProtectedRoute
              path='/investigation/:account?'
              component={Investigation}
            />
            <ProtectedRoute path='/case-reports' component={CaseReportList} />
            <ProtectedRoute path='/case-report/:case' component={CaseReport} />
            <ProtectedRoute path='/ai/briefing' component={AIBriefing} />
            <ProtectedRoute path='/whats-new' component={WhatsNew} />
            <ProtectedRoute path='/card-account' component={CardAccount} />
            <Route path='*' component={NotFound} />
          </Switch>
        </div>
      </React.Suspense>
    </React.Suspense>
  );
};

const ConnectedPage = withRouter(
  connect((state: AppState) => ({
    ...state,
    permissions: getPermissions(state),
  }))(Page),
);

// If the <head> block contains a <base> tag, let's respect that as the base URL for the router
let baseHref = '';
if (document.getElementById('base-href')) {
  baseHref =
    document.getElementById('base-href')?.getAttribute('href')?.slice(0, -1) ??
    '';
}

export const muiCache = createCache({
  key: 'mui',
  prepend: false,
});

const App = () => (
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Router basename={baseHref}>
          <CacheProvider value={muiCache}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ConnectedPage />
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
        </Router>
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

export default App;
